'use client';

import { c as _c } from "react/compiler-runtime";
import type { ListRecommendPolicyDto } from '__generated__/api/auth';
import { DropdownMenu, Spinner } from '@common/components';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { useTransition } from 'react';
import { AuthQueryOptions } from '@/api';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { isAvailablePolicy, isDeadlinePolicy } from '../_lib/utils';
import type { FilterType, SortType } from '../_types/types';
import { Card } from './Card';
import { EmptyState } from './EmptyState';
const SORT_OPTIONS: Array<{
  label: string;
  value: SortType;
}> = [{
  label: '웰로추천순',
  value: 'PRIORITY'
}, {
  label: '스크랩순',
  value: 'SCRAP'
}, {
  label: '조회순',
  value: 'READ'
}, {
  label: '마감 임박순',
  value: 'DUE_DATE_END'
}];
interface PolicyListProps {
  filter?: FilterType;
  sort: SortType;
}
const filterPolicies = (policies: ListRecommendPolicyDto[], filter?: FilterType): ListRecommendPolicyDto[] => {
  if (!filter || filter === 'available') {
    return policies;
  }
  const availablePolicies = policies.filter(policy => isAvailablePolicy(policy.dday));
  switch (filter) {
    case 'new':
      return availablePolicies.filter(policy => policy.newPolicy === true);
    case 'deadline':
      return availablePolicies.filter(policy => isDeadlinePolicy(policy.dday));
    case 'scrap':
      return policies.filter(policy => policy.wishlistYN === true);
    default:
      return policies;
  }
};
export const PolicyList = t0 => {
  const $ = _c(45);
  const {
    filter,
    sort
  } = t0;
  const {
    push
  } = useCustomRouter();
  const searchParams = useSearchParams();
  const [isPending, startTransition] = useTransition();
  let t1;
  if ($[0] !== sort) {
    t1 = AuthQueryOptions["get:/wello/v2/auth/recommend-policy"]({
      query: {
        recommendPolicySort: sort,
        recommendViewType: "LIST"
      }
    });
    $[0] = sort;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = {
      ...t1
    };
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    data
  } = useSuspenseQuery(t2);
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[4] !== data?.context?.contents || $[5] !== filter || $[6] !== isPending || $[7] !== push || $[8] !== searchParams || $[9] !== sort) {
    t6 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const allPolicies = data?.context?.contents ?? [];
      const filteredPolicies = filterPolicies(allPolicies, filter);
      let t7;
      if ($[14] !== push || $[15] !== searchParams || $[16] !== sort) {
        t7 = value => {
          if (sort === value) {
            return;
          }
          startTransition(() => {
            const params = new URLSearchParams(searchParams?.toString());
            params.set("sort", value);
            push(`?${params.toString()}`);
          });
        };
        $[14] = push;
        $[15] = searchParams;
        $[16] = sort;
        $[17] = t7;
      } else {
        t7 = $[17];
      }
      const handleSortChange = t7;
      if (filteredPolicies.length === 0) {
        let t8;
        if ($[18] !== handleSortChange || $[19] !== isPending || $[20] !== sort) {
          t8 = <DropdownMenu type="text" options={SORT_OPTIONS} value={sort} onChange={handleSortChange} disabled={isPending} contentClassName="!min-w-150" align="start" />;
          $[18] = handleSortChange;
          $[19] = isPending;
          $[20] = sort;
          $[21] = t8;
        } else {
          t8 = $[21];
        }
        let t9;
        if ($[22] !== isPending) {
          t9 = isPending && <Spinner size={16} />;
          $[22] = isPending;
          $[23] = t9;
        } else {
          t9 = $[23];
        }
        let t10;
        if ($[24] !== t8 || $[25] !== t9) {
          t10 = <div className="flex items-center justify-between px-20 pb-12"><div className="flex items-center gap-8">{t8}{t9}</div></div>;
          $[24] = t8;
          $[25] = t9;
          $[26] = t10;
        } else {
          t10 = $[26];
        }
        let t11;
        if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
          t11 = <EmptyState />;
          $[27] = t11;
        } else {
          t11 = $[27];
        }
        let t12;
        if ($[28] !== t10) {
          t12 = <>{t10}{t11}</>;
          $[28] = t10;
          $[29] = t12;
        } else {
          t12 = $[29];
        }
        t6 = t12;
        break bb0;
      }
      let t8;
      if ($[30] !== handleSortChange || $[31] !== isPending || $[32] !== sort) {
        t8 = <DropdownMenu type="text" options={SORT_OPTIONS} value={sort} onChange={handleSortChange} disabled={isPending} contentClassName="!min-w-150" align="start" />;
        $[30] = handleSortChange;
        $[31] = isPending;
        $[32] = sort;
        $[33] = t8;
      } else {
        t8 = $[33];
      }
      let t9;
      if ($[34] !== isPending) {
        t9 = isPending && <Spinner size={16} />;
        $[34] = isPending;
        $[35] = t9;
      } else {
        t9 = $[35];
      }
      if ($[36] !== t8 || $[37] !== t9) {
        t5 = <div className="flex items-center justify-between px-20 pb-12"><div className="flex items-center gap-8">{t8}{t9}</div></div>;
        $[36] = t8;
        $[37] = t9;
        $[38] = t5;
      } else {
        t5 = $[38];
      }
      t3 = "flex min-h-[calc(100vh-330px)] flex-col gap-16 px-20 pb-40";
      t4 = filteredPolicies.map((policy, index) => <li key={policy.id}><Card policy={policy} isLast={index === filteredPolicies.length - 1} /></li>);
    }
    $[4] = data?.context?.contents;
    $[5] = filter;
    $[6] = isPending;
    $[7] = push;
    $[8] = searchParams;
    $[9] = sort;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t3 = $[10];
    t4 = $[11];
    t5 = $[12];
    t6 = $[13];
  }
  if (t6 !== Symbol.for("react.early_return_sentinel")) {
    return t6;
  }
  let t7;
  if ($[39] !== t3 || $[40] !== t4) {
    t7 = <ul className={t3}>{t4}</ul>;
    $[39] = t3;
    $[40] = t4;
    $[41] = t7;
  } else {
    t7 = $[41];
  }
  let t8;
  if ($[42] !== t5 || $[43] !== t7) {
    t8 = <>{t5}{t7}</>;
    $[42] = t5;
    $[43] = t7;
    $[44] = t8;
  } else {
    t8 = $[44];
  }
  return t8;
};